import { Gateway } from 'src/types/Gateway';
import { get, post } from '../utils';
import { DEAD_AFTER_TOLERANCES } from 'src/utils/deviceOnline';

export async function getGateway(deviceId: string) {
  return get<Gateway>(`gateway/${deviceId}`);
}

export type GatewayStatus = {
  lastUplinkReceivedAt?: string;
  isConnected?: boolean;
  vpnIpAddress?: string;
};

export async function getGatewayStatus(deviceId: string): Promise<GatewayStatus> {
  return get<Gateway>(`gateway/${deviceId}/status`);
}

export async function searchGateways({ networkId, simCardIdentifier }: { networkId?: string, simCardIdentifier?: string }) {
  return get<Gateway[]>(`gateway`, { networkId, simCardIdentifier });
}

export interface NewGateway extends Omit<Gateway, 'id' | 'deviceId'> {
  serialNumber: string;
  sku: string;
  region?: string;
}

export function createGateway(newGateway: NewGateway): Promise<Gateway> {
  return post<Gateway>('/gateway', {
    ...newGateway,
  });
}

export type GatewayConnectionHistory = {
  connection_status: 1 | 0;
  timestamp: string;
};

/** Get the last 24 hours of gateway connections */
export async function getGatewayConnectionHistory(
  gatewayId: string,
  opts?: { startTimestamp?: string }
): Promise<GatewayConnectionHistory[]> {
  return get<GatewayConnectionHistory>(`gateway/${gatewayId}/connectionhistory`, opts);
}

export type SeenDeviceInfo = {
  lastSeenAt: string;
  messageCount: number | null;
  firstSeenAt: string | null;
  isLegacy?: boolean;
};

/**
 * The longest silence the catalogue lets any device type tolerate, enforced on the API's catalogue by
 * an invariant test (main-api `models/tests/deviceTypeCatalog-test.ts`, MAX_OFFLINE_AFTER_MINUTES).
 * The largest value served today is 26 h (Genus IntelliLog).
 */
const CATALOGUE_TOLERANCE_CAP_HOURS = 36;

/**
 * How far back a per-device "seen by this gateway" list reaches, for pages that ask which devices a
 * gateway is hearing and when each was last heard.
 *
 * The API anchors `windowDays` at the gateway's own newest sighting, not at now, so this bounds how
 * long before that a device can have last been heard and still be listed at all. The lists must be
 * able to hold every device that is not yet `dead`, so the window must reach the dead boundary of the
 * slowest type the catalogue allows: DEAD_AFTER_TOLERANCES x the 36 h cap = 108 h, rounded up to whole days
 * = 5. Derived rather than written down, so it follows either number. Revisit the cost if either
 * grows, and note the API rejects a window over 90 days.
 *
 * Cost: every day is one more day of the gateway's network rows scanned (KEYS_ONLY index, fully
 * paginated, uncached), so 5 days is five times the scan of the old 1-day window. That is still a
 * sixth of the 30-day scan GatewayExpandedView runs per gateway. The response is one entry per
 * device, so its size barely moves.
 */
export const SEEN_DEVICES_WINDOW_DAYS = Math.ceil((DEAD_AFTER_TOLERANCES * CATALOGUE_TOLERANCE_CAP_HOURS) / 24);

type SeenDeviceApiValue =
  | string
  | {
      lastSeenAt?: string;
      messageCount?: number | null;
      firstSeenAt?: string | null;
    };

function normaliseSeenDevices(response: Record<string, SeenDeviceApiValue>): Record<string, SeenDeviceInfo> {
  return Object.fromEntries(
    (Object.entries(response) as [string, SeenDeviceApiValue][]).map(([deviceId, value]) => {
      if (typeof value === 'string') {
        return [
          deviceId,
          {
            lastSeenAt: value,
            messageCount: null,
            firstSeenAt: null,
            isLegacy: true,
          },
        ];
      }

      const lastSeenAt = typeof value?.lastSeenAt === 'string' ? value.lastSeenAt : '';

      return [
        deviceId,
        {
          lastSeenAt,
          messageCount: typeof value.messageCount === 'number' ? value.messageCount : null,
          firstSeenAt: value.firstSeenAt ?? null,
          isLegacy: !lastSeenAt,
        },
      ];
    })
  );
}

export async function getGatewaySeenDevicesWindow(
  gatewayId: string,
  windowDays: number
): Promise<Record<string, SeenDeviceInfo>> {
  const response = await get<Record<string, SeenDeviceApiValue>>(`gateway/${gatewayId}/seendevices`, { windowDays });

  return normaliseSeenDevices(response);
}

/**
 * Devices a gateway heard since an absolute ISO time (`?since=`). `gatewayId` is the gateway's
 * DEVICE id (the Gateway table's primary key). LoRa ingestion records only the best-SNR gateway
 * per uplink, so a working gateway that is never anyone's best gateway answers with nothing.
 */
export async function getGatewaySeenDevicesSince(gatewayId: string, since: string): Promise<Record<string, SeenDeviceInfo>> {
  const response = await get<Record<string, SeenDeviceApiValue>>(`gateway/${gatewayId}/seendevices`, { since });

  return normaliseSeenDevices(response ?? {});
}
